// ChainsDrawer — the WRITE surface for the accountability chains at ONE scope
// (ADR-026 §C), every level's (zone · cluster · site · team today; project ·
// warehouse · vehicle pool when their routes land): a StagedDrawer that swaps
// between the scope's responsibilities with their chains and ONE responsibility's
// ordered chain editor (no nested overlay). The STAGE stands on its own —
// `useChainEdit` (the draft seeded from the read, dirty, the save through the
// one wire with the backing consent: ADR-052 §5 the scope grant, ADR-083 §3 the
// team membership — a 409 ASSIGNEE_NOT_BACKED opens the confirm, confirming
// re-saves with the consent) and `ChainEditStage` (the editor over the person
// lookup's `accountable` rule of the scope, each row saying whether the person
// is backed, the chip "Will be granted scope" / "Will be added to the team", the
// dialog by the backing kind) — so a person's page composes the same stage over
// the person's chains. The draft opens on ONE read — the chains with their PLACE's
// stamp (ADR-084 §3 · §4 — a chain is a set beneath its place), and the save
// carries that stamp back; "changed" is judged against the chain as the editor
// opened it, never the live row; a refusal is the drawer's one line (ADR-084 §6 —
// Load the latest re-reads the chains and replaces the draft). A
// CLOSED place (the host's word: a zone or a cluster switched off, a site
// decommissioned, a team disabled) takes only a removal (ADR-080 §5): the editor's
// remove face and one line saying so. Opened only by a viewer who can assign at the
// level: the host gates the launcher. The words: `common:chains.*` (the drawer),
// `common:chain.*` (the stage's chips and row labels), `common:accountability.*`
// (the consent dialog).

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ScopeChain, ScopeChainsResponse } from '@ems-app/schemas/accountability/responses';
import { chainsKeys, fetchScopeChains, useScopeChains, useSetAssignmentChain, type ChainLevel, type ChainScopeId } from '../../hooks/useChains.js';
import { statusOf } from '../../lib/refusal.js';
import { useResetOnOpen } from '../../hooks/useResetOnOpen.js';
import { useResponsibilityChainSave } from '../../hooks/useResponsibilityChainSave.js';
import { personSource, useNamedPeople } from '../../hooks/useUsers.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Spinner } from '../ui/Spinner.js';
import { assignmentRowLabels, chainLabels, toAssignmentItems, toChainPerson } from './accountabilityDisplay.js';
import { BackingConfirmDialog } from './BackingConfirmDialog.js';
import { readLatest } from './forms/readLatest.js';
import { useRefusalLine, type RefusalLineState } from './forms/useRefusalLine.js';
import { useAccountableFace, usePersonFace } from './lookupFaces.js';
import { ResponsibilityAssignmentList } from './ResponsibilityAssignmentList.js';
import { ResponsibilityChainEditor, type ChainPerson, type ResponsibilityChainEditorProps } from './ResponsibilityChainEditor.js';
import { StagedDrawer, type StagedGuard } from './StagedDrawer.js';

export interface ChainScope {
  level: ChainLevel;
  id: ChainScopeId;
  /** The place's name — the drawer's description, the consent's "{{place}}". */
  name: string;
  /** The place is closed (switched off · decommissioned · disabled) — its chains take only a removal (ADR-080 §5). */
  closed?: boolean;
}


/** What backs a holder at a level — fixed by the level, never a host's word: their SCOPE, or, on a team, their
 *  MEMBERSHIP (ADR-083 §3: the chip "Will be added to the team", the dialog that adds them). */
export function backingOf(level: ChainLevel): 'scope' | 'membership' {
  return level === 'team' ? 'membership' : 'scope';
}

export interface ChainPeople {
  source: ResponsibilityChainEditorProps['source'];
  people: ReadonlyMap<string, ChainPerson>;
  nameOf: (id: string) => string;
}

/** The people a chain surface shows — the chains' holders and the draft's — NAMED by the person lookup's rows of given
 *  ids on the scope's `accountable` rule (the cache the picker writes to): a disabled holder kept and greyed, a holder
 *  the backing does not reach wearing the chip, a code the server answers absent reading "…". Nothing loads while
 *  `enabled` is off (a closed drawer). */
export function useChainPeople(projectId: string, scope: ChainScope | null, ids: readonly string[], enabled: boolean): ChainPeople {
  const personFace = usePersonFace();
  const accountableFace = useAccountableFace({ backing: scope ? backingOf(scope.level) : 'scope' });
  const level = scope?.level;
  const scopeId = scope ? String(scope.id) : undefined;
  const source = useMemo(() => personSource(projectId, 'accountable', accountableFace, { level, scope: scopeId }), [projectId, accountableFace, level, scopeId]);
  const { byId } = useNamedPeople(projectId, ids, { rule: 'accountable', level, scope: scopeId, enabled: enabled && scope !== null });
  const people = useMemo(() => new Map([...byId.values()].map((row) => [row.id, toChainPerson(row, personFace)] as const)), [byId, personFace]);
  const nameOf = useCallback((id: string): string => people.get(id)?.name ?? '…', [people]);
  return { source, people, nameOf };
}

export interface ChainEdit {
  /** The responsibility's chain as read — undefined until the read is in or while nothing is chosen. */
  chain: ScopeChain | undefined;
  draft: string[];
  setDraft: (next: string[]) => void;
  /** The draft differs from the chain as the editor opened it (order counts) — never the live row. */
  dirty: boolean;
  /** A save in flight. */
  busy: boolean;
  save: () => void;
  /** The refusal line — the drawer draws it (`StagedDrawer`'s `line`). */
  line: RefusalLineState;
  backingPrompt: string[] | null;
  confirmBacking: () => void;
  dismissBacking: () => void;
  /** Drop the draft and the transient state (a close, a Back). */
  reset: () => void;
}

/** ONE responsibility's chain at ONE scope as a draft: seeded from the read once it is in, saved whole through the one
 *  wire with the place's stamp, the backing consent the shared flow's, a refusal on the line. The host says which
 *  responsibility (null = none) and what to do once saved. */
export function useChainEdit(opts: { projectId: string; scope: ChainScope | null; responsibilityId: number | null; read: ScopeChainsResponse | undefined; onSaved: () => void }): ChainEdit {
  const { projectId, scope, responsibilityId, read, onSaved } = opts;
  const chains = read?.items;
  const { t: tErrors } = useTranslation('errors');
  const qc = useQueryClient();
  const put = useSetAssignmentChain(projectId);
  const [draft, setDraft] = useState<string[]>([]);
  // The chain as the editor opened it — the dirty word's baseline (a refetch while the drawer is open never makes an
  // untouched draft "changed").
  const [opened, setOpened] = useState<string[]>([]);
  // The place's stamp taken WITH the seed, from the same read — never a later one (ADR-084 §3).
  const [openedStamp, setOpenedStamp] = useState<number | undefined>(undefined);
  const [seededFor, setSeededFor] = useState<number | null>(null);
  const chain = responsibilityId !== null ? chains?.find((c) => c.responsibility_id === responsibilityId) : undefined;
  // Seed the draft from the chosen responsibility's current chain, once the read is in (state-from-props).
  if (responsibilityId !== null && seededFor !== responsibilityId && chains) {
    const ids = [...(chain?.member_ids ?? [])];
    setDraft(ids);
    setOpened(ids);
    setOpenedStamp(read?.row_version ?? undefined);
    setSeededFor(responsibilityId);
  }
  const line = useRefusalLine({
    edit: true,
    // Load the latest: the chains and their place's stamp re-read quietly; the draft is REPLACED by the fresh chain (a
    // list cannot keep the typing and take the rest). The place gone meanwhile is the gone line.
    reload: async () => {
      if (scope === null) return false;
      try {
        await readLatest(qc, chainsKeys.scope(projectId, scope.level, scope.id), () => fetchScopeChains(projectId, scope.level, scope.id, true));
      } catch (err) {
        if (statusOf(err) === 404) return false;
        throw err;
      }
      setSeededFor(null);
      return true;
    },
  });
  const flow = useResponsibilityChainSave({
    submit: (grantBacking) =>
      scope === null || responsibilityId === null ? Promise.resolve() : put.mutateAsync({ level: scope.level, scopeId: scope.id, responsibilityId, userIds: draft, grantBacking, rowVersion: openedStamp }),
    // Saved: the draft is no longer this responsibility's — the next pick of it reseeds from the re-read (the save
    // resolves once the scope's chains are read again, so the seed is the saved chain with the place's new stamp).
    onSaved: () => {
      setSeededFor(null);
      onSaved();
    },
    onRefused: line.refuse,
    tErrors,
  });
  const dirty = seededFor !== null && (draft.length !== opened.length || draft.some((id, i) => id !== opened[i]));
  return {
    chain,
    draft,
    setDraft,
    dirty,
    busy: put.isPending,
    save: () => {
      line.dismiss();
      flow.save();
    },
    line,
    backingPrompt: flow.backingPrompt,
    confirmBacking: flow.confirmBacking,
    dismissBacking: flow.dismissBacking,
    reset: () => {
      setDraft([]);
      setOpened([]);
      setOpenedStamp(undefined);
      setSeededFor(null);
      line.dismiss();
      flow.reset();
    },
  };
}

/** The stage: the ordered editor over the scope's people (a closed place's: the remove face), the backing consent by
 *  the backing kind. The refusal is the frame's line. */
export function ChainEditStage({ edit, scope, people }: { edit: ChainEdit; scope: ChainScope; people: ChainPeople }) {
  const { t } = useTranslation('common');
  const backing = backingOf(scope.level);
  const labels = useMemo(() => ({ ...chainLabels(t), needsScope: { chip: t(backing === 'membership' ? 'chain.needsMembership.chip' : 'chain.needsScope.chip') } }), [t, backing]);
  return (
    <>
      <ResponsibilityChainEditor value={edit.draft} onChange={edit.setDraft} people={people.people} source={people.source} labels={labels} mode={edit.line.gone ? 'read' : scope.closed ? 'remove' : 'edit'} />
      <BackingConfirmDialog variant={backing} open={edit.backingPrompt !== null} place={scope.name} names={(edit.backingPrompt ?? []).map(people.nameOf)} onConfirm={edit.confirmBacking} onClose={edit.dismissBacking} />
    </>
  );
}

export interface ChainsDrawerProps {
  projectId: string;
  /** The scope whose chains are edited — null renders nothing (a list host with no row chosen yet). */
  scope: ChainScope | null;
  open: boolean;
  onClose: () => void;
  /** Jump straight to this responsibility's editor on open (a row entry): the editor is then the root view, its
   *  secondary button Cancel; a list entry shows Back. */
  initialResponsibilityId?: number | null;
  /** A chain saved — the place's stamp moved by the host's own hand (a host holding that stamp — the site wizard —
   *  takes the new one). */
  onSaved?: () => void;
}

export function ChainsDrawer({ projectId, scope, open, onClose, initialResponsibilityId = null, onSaved }: ChainsDrawerProps) {
  const { t } = useTranslation('common');
  const chainsQ = useScopeChains(projectId, scope?.level ?? 'site', open && scope ? scope.id : null);
  const [editingId, setEditingId] = useState<number | null>(initialResponsibilityId);
  const [directEntry, setDirectEntry] = useState(initialResponsibilityId !== null);
  // A direct entry has no list behind it — a save is done, so close; a list entry returns to the list so the operator
  // can assign the next one.
  const edit = useChainEdit({
    projectId,
    scope,
    responsibilityId: editingId,
    read: chainsQ.data,
    onSaved: () => {
      onSaved?.();
      if (directEntry) onClose();
      else setEditingId(null);
    },
  });
  const ids = useMemo(() => [...new Set([...(chainsQ.data?.items ?? []).flatMap((c) => c.member_ids), ...(editingId !== null ? edit.draft : [])])], [chainsQ.data, edit.draft, editingId]);
  const people = useChainPeople(projectId, scope, ids, open);
  useResetOnOpen(open, (isOpen) => {
    setEditingId(isOpen ? initialResponsibilityId : null);
    setDirectEntry(isOpen ? initialResponsibilityId !== null : false);
    edit.reset();
  });
  if (!scope) return null;
  const editing = edit.chain;
  const backToList = () => {
    setEditingId(null);
    edit.reset();
  };
  const footer = (g: StagedGuard) =>
    editing ? (
      <>
        <Button variant="secondary" onClick={directEntry ? g.requestClose : g.guarded(backToList)}>
          {directEntry ? t('actions.cancel') : t('actions.back')}
        </Button>
        <Button variant="primary" onClick={edit.save} loading={edit.busy} disabled={edit.line.gone}>
          {t('actions.save')}
        </Button>
      </>
    ) : (
      <Button variant="primary" onClick={onClose}>
        {t('actions.close')}
      </Button>
    );
  return (
    <StagedDrawer open={open} onClose={onClose} title={editing ? editing.name : t('chains.manageTitle')} description={editing ? `${scope.name} · ${t('chains.ordered')}` : scope.name} dirty={edit.dirty} busy={edit.busy} line={edit.line} footer={footer}>
      {scope.closed && (
        <Alert variant="info" className="mb-4">
          {t('chains.closed')}
        </Alert>
      )}
      {chainsQ.isPending ? (
        <div className="flex min-h-[8rem] items-center justify-center" role="status" aria-live="polite">
          <Spinner />
        </div>
      ) : chainsQ.isError && chainsQ.data === undefined ? (
        <Alert variant="danger">{t('chains.error')}</Alert>
      ) : editing ? (
        <ChainEditStage edit={edit} scope={scope} people={people} />
      ) : (
        <ResponsibilityAssignmentList items={toAssignmentItems(chainsQ.data?.items ?? [], people.nameOf)} onManage={(id) => setEditingId(Number(id))} empty={t('chains.empty')} rowLabels={assignmentRowLabels(t)} />
      )}
    </StagedDrawer>
  );
}
